/**
 * SwipeCarousel — Instagram Reels / TikTok-style horizontal snap-scroller.
 * On mobile renders as a swipeable carousel where each card snaps to ~88vw
 * with smooth scroll-snap behaviour and a pager-dot strip below. On sm+
 * degrades to the original responsive grid so desktop / tablet users keep
 * their broader overview.
 *
 * Mirrors the pattern proven on HomePage UpcomingDropsCarousel so the Drops
 * page (active + sold-out sections) gets the same buttery swipe feel
 * without duplicating logic.
 *
 * Props:
 *   items: array of any (the render function decides how to draw each one)
 *   renderItem: (item, idx) => ReactNode — REQUIRED
 *   gridCols: "2-3-4" | "1-2-3" | "2-4"   tailwind grid-cols at sm+
 *   mobileWidth: tailwind width class for the snap card on mobile, default "w-[88vw]"
 *   tabletWidth: tailwind width class for the snap card at sm-lg when
 *                keepCarouselUntilXl is true. Default "sm:w-[46vw]" → 2-up.
 *   keepCarouselUntilXl: when true, stay in horizontal-snap mode all the
 *                way through tablet sizes (sm/md/lg) and only switch to
 *                grid at xl (1280px+). Mobile renders 1-up @88vw, tablet
 *                renders 2-up @46vw. Defaults to false to preserve the
 *                original grid-at-sm behaviour for non-opted-in consumers.
 *   swipeHintKey: optional localStorage key. When provided, renders the
 *                shared <SwipeHintPill> in the bottom-right of the carousel.
 *                The pill fades out on first scroll / dot tap / 6s timeout
 *                and is hidden once the carousel becomes a grid.
 *   testId: data-testid for the scroll wrapper
 */
import React, { useEffect, useRef, useState } from "react";
import SwipeHintPill from "./SwipeHintPill";

const GRID_PRESETS = {
  // bumped 2026-05-26 per user: cards were rendering 3-wide on iPad/Pro Max
  // and felt cramped (truncated titles, squeezed JOIN QUEUE button). Hold
  // 2 cols on tablet, only go denser on true desktop (xl = 1280px+).
  "2-3-4": "sm:grid sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4",
  "1-2-3": "sm:grid sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3",
  "2-4":   "sm:grid sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4",
};

// When keepCarouselUntilXl=true we suppress the sm:grid switch and only
// flip to a real grid at xl. Keeps the tablet experience as a 2-up swipe
// carousel like Instagram Reels.
const XL_GRID = "xl:grid xl:grid-cols-3";

const SwipeCarousel = ({
  items = [],
  renderItem,
  gridCols = "2-3-4",
  mobileWidth = "w-[88vw]",
  tabletWidth = "sm:w-[46vw] lg:w-[46vw]",
  keepCarouselUntilXl = false,
  swipeHintKey = null,
  testId = "swipe-carousel",
  // Pager dots default OFF (changed 2026-06 per user — the dot strip
  // under each carousel was reading as "noisy bubbles" on the Home +
  // Drops mobile views, especially when 3+ rails stacked vertically.
  // Consumers that genuinely want dots back can opt in explicitly via
  // `showDots={true}` (e.g., a single hero carousel).
  showDots = false,
  // Progress bar replaces the dots — 1px brand-gradient fill that
  // grows left→right as the user scrolls the carousel. Same visual
  // language as Instagram Stories. Defaults ON so every consumer
  // inherits it automatically. Set false to render nothing under the
  // carousel.
  showProgress = true,
}) => {
  const scrollRef = useRef(null);
  const hintRef = useRef(null);
  const [activeIdx, setActiveIdx] = useState(0);
  // 0 → 1 fraction of how far through the horizontal scroll the user
  // is. Updated on every scroll tick alongside activeIdx so the
  // progress bar fills smoothly while swiping (not stepped per card).
  const [scrollFrac, setScrollFrac] = useState(0);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    let raf = 0;
    const onScroll = () => {
      // First scroll = the user has discovered swipe. Drop the hint.
      hintRef.current?.dismiss();
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const children = Array.from(el.children).filter((c) => c.dataset?.swipeTile === "1");
        if (children.length === 0) return;
        const scrollMid = el.scrollLeft + el.clientWidth / 2;
        let nearest = 0;
        let nearestDist = Infinity;
        children.forEach((c, i) => {
          const mid = c.offsetLeft + c.offsetWidth / 2;
          const d = Math.abs(mid - scrollMid);
          if (d < nearestDist) { nearestDist = d; nearest = i; }
        });
        setActiveIdx(nearest);
        // Smooth scroll fraction for the progress bar — clamp to [0,1]
        // so rubber-band scroll past the ends doesn't push the bar
        // beyond the rail.
        const maxScroll = el.scrollWidth - el.clientWidth;
        const frac = maxScroll > 0 ? Math.min(1, Math.max(0, el.scrollLeft / maxScroll)) : 0;
        setScrollFrac(frac);
      });
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => { el.removeEventListener("scroll", onScroll); cancelAnimationFrame(raf); };
  }, [items.length]);

  const scrollToIdx = (i) => {
    hintRef.current?.dismiss();
    const el = scrollRef.current;
    if (!el) return;
    const child = el.children[i];
    if (child && child.dataset?.swipeTile === "1") {
      child.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
    }
  };

  if (!items || items.length === 0) return null;
  const grid = keepCarouselUntilXl
    ? `${XL_GRID} xl:overflow-visible xl:snap-none`
    : GRID_PRESETS[gridCols] || GRID_PRESETS["2-3-4"];
  // When we stay in carousel mode through tablet, the cards need a sized
  // width at sm-lg too (2-up) and `w-auto` only at xl when grid kicks in.
  const cardWidthClasses = keepCarouselUntilXl
    ? `${mobileWidth} ${tabletWidth} xl:w-auto`
    : `${mobileWidth} sm:w-auto`;
  // Dots/hint only hide once we leave carousel mode entirely.
  const carouselHideAt = keepCarouselUntilXl ? "xl:hidden" : "sm:hidden";

  return (
    <>
      <div className="relative">
        <div
          ref={scrollRef}
          className={`flex ${grid} ${keepCarouselUntilXl ? "" : "sm:overflow-visible"} gap-4 sm:gap-5 overflow-x-auto snap-x snap-mandatory ${keepCarouselUntilXl ? "" : "sm:snap-none"} -mx-4 ${keepCarouselUntilXl ? "xl:mx-0 xl:px-0" : "sm:mx-0 sm:px-0"} px-4 pb-2 ${keepCarouselUntilXl ? "xl:pb-0" : "sm:pb-0"} scrollbar-hide`}
          style={{ scrollbarWidth: "none", msOverflowStyle: "none", WebkitOverflowScrolling: "touch" }}
          data-testid={testId}
        >
          {items.map((item, idx) => (
            <div
              key={item?.id ?? idx}
              data-swipe-tile="1"
              className={`flex-shrink-0 ${cardWidthClasses} snap-center`}
            >
              {renderItem(item, idx)}
            </div>
          ))}
        </div>
        {swipeHintKey && items.length > 1 && (
          <SwipeHintPill
            ref={hintRef}
            storageKey={swipeHintKey}
            hideAt={carouselHideAt}
            testId={`${testId}-swipe-hint`}
          />
        )}
      </div>
      {/* Pager dots — carousel-mode only, hidden when only one card.
          MAX_DOTS cap: when more items than dots, each dot represents a
          bucket of items. Active bucket lights up; tapping a dot jumps
          you to the first item of that bucket. Why: previously rendered
          one dot per item, which produced 10-20 dots on dense carousels
          (homepage upcoming drops, /play active drops) — looked like
          shit on mobile per direct admin feedback. */}
      {showDots && items.length > 1 && (() => {
        const MAX_DOTS = 5;
        const totalDots = Math.min(items.length, MAX_DOTS);
        const bucketSize = Math.max(1, Math.ceil(items.length / totalDots));
        const activeDotIdx = Math.min(totalDots - 1, Math.floor(activeIdx / bucketSize));
        return (
          <div className={`${carouselHideAt} flex justify-center gap-1.5 mt-3`} data-testid={`${testId}-dots`}>
            {Array.from({ length: totalDots }).map((_, i) => (
              <button
                key={i}
                type="button"
                aria-label={
                  totalDots === items.length
                    ? `Go to slide ${i + 1}`
                    : `Jump to slides ${i * bucketSize + 1}-${Math.min((i + 1) * bucketSize, items.length)}`
                }
                onClick={() => scrollToIdx(i * bucketSize)}
                className={`h-1.5 rounded-full transition-all ${i === activeDotIdx ? "w-6 brand-gradient-bg" : "w-1.5 bg-white/25"}`}
              />
            ))}
          </div>
        );
      })()}
      {/* Progress bar — Instagram-Stories-style 1px fill that grows
          left→right as the user scrolls. Sits in the same vertical
          slot as the (removed) dots so layout doesn't shift. Hidden
          when there's only one card (nothing to scroll) or when the
          carousel has flattened into a grid. Width is taken from the
          page edge so it visually anchors the rail. */}
      {showProgress && !showDots && items.length > 1 && (
        <div
          className={`${carouselHideAt} mt-3 -mx-4 px-4`}
          data-testid={`${testId}-progress`}
        >
          <div className="h-[2px] w-full bg-white/10 rounded-full overflow-hidden">
            <div
              className="h-full brand-gradient-bg rounded-full transition-[width] duration-150 ease-out"
              style={{ width: `${Math.max(8, scrollFrac * 100)}%` }}
              aria-hidden="true"
            />
          </div>
        </div>
      )}
    </>
  );
};

export default SwipeCarousel;
